import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "@/components/Modal";
import { Input } from "@/components/Input";
import { tenantApi } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { ImageCropModal } from "@/components/ImageCropModal";
import { Icon } from "@/components/Icon";

interface NewCompanyModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
}
export const NewCompanyModal = ({
    isOpen,
    onClose,
    onSuccess,
}: NewCompanyModalProps) => {
    const { t } = useTranslation("global");
    const { showSuccess, showError } = useToast();

    const [id, setId] = useState("");
    const [name, setName] = useState("");
    const [logoUrl, setLogoUrl] = useState("");
    const [brandColor, setBrandColor] = useState("#31628d");
    const [loading, setLoading] = useState(false);
    const [logoFile, setLogoFile] = useState<File | null>(null);
    const [logoPreview, setLogoPreview] = useState("");
    const [isLogoCropOpen, setIsLogoCropOpen] = useState(false);
    const logoInputRef = useRef<HTMLInputElement>(null);

    const [idError, setIdError] = useState(false);
    const [nameError, setNameError] = useState(false);

    const idRef = useRef<HTMLDivElement>(null);
    const nameRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (isOpen) {
            setId("");
            setName("");
            setLogoUrl("");
            setLogoFile(null);
            setBrandColor("#31628d");
            setIdError(false);
            setNameError(false);
        }
    }, [isOpen]);

    useEffect(() => {
        if (!logoFile) {
            setLogoPreview("");
            return;
        }

        const preview = URL.createObjectURL(logoFile);
        setLogoPreview(preview);

        return () => URL.revokeObjectURL(preview);
    }, [logoFile]);

    const handleSubmit = async () => {
        const missingId = !id.trim();
        const missingName = !name.trim();

        if (missingId || missingName) {
            setIdError(missingId);
            setNameError(missingName);

            showError(t("companies.validation_required"));

            const firstMissing = missingId ? idRef : nameRef;
            firstMissing.current?.scrollIntoView({
                behavior: "smooth",
                block: "center",
            });

            return;
        }

        setLoading(true);
        try {
            const response = await tenantApi.create({
                database: id,
                name,
                logo_url: logoUrl.trim() || null,
                brand_color: brandColor,
            });
            const companyId = response.data?.company?.id;
            if (companyId && logoFile) {
                const logoResponse = await tenantApi.uploadLogo(companyId, logoFile);
                setLogoUrl(logoResponse.data?.company?.logo_url || "");
            }
            showSuccess(t("companies.created"));
            onSuccess();
            onClose();
        } catch (error) {
            showError(
                error instanceof Error
                    ? error.message
                    : t("companies.error_create")
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={t("companies.new_title")}
            onSubmit={handleSubmit}
            submitLabel={t("common.save")}
            submittingLabel={t("common.saving")}
            cancelLabel={t("common.cancel")}
            isSubmitting={loading}
            maxWidth="md"
        >
            <div className="space-y-4 p-4">
                <div ref={idRef}>
                    <Input
                        label={t("companies.id")}
                        value={id}
                        onChange={(e) => {
                            setId(e.target.value);
                            if (idError) setIdError(false);
                        }}
                        placeholder={t("companies.id_placeholder")}
                        required
                        className={
                            idError ? "animate-error-blink rounded-xl" : ""
                        }
                    />
                </div>
                <div ref={nameRef}>
                    <Input
                        label={t("companies.name")}
                        value={name}
                        onChange={(e) => {
                            setName(e.target.value);
                            if (nameError) setNameError(false);
                        }}
                        placeholder=""
                        required
                        className={
                            nameError ? "animate-error-blink rounded-xl" : ""
                        }
                    />
                </div>
                <div>
                    <div className="mb-3 text-center text-sm font-semibold text-slate-700 dark:text-zinc-200">
                        {t("companies.logo")}
                    </div>
                    <input
                        ref={logoInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        className="hidden"
                        onChange={(event) => {
                            const file = event.target.files?.[0];
                            if (file) {
                                setLogoFile(file);
                                setIsLogoCropOpen(true);
                            }
                            event.target.value = "";
                        }}
                    />
                    <button
                        type="button"
                        className="group relative mx-auto flex h-24 w-24 items-center justify-center overflow-visible rounded-full border-2 border-primary bg-primary text-2xl font-black text-white shadow-md"
                        onClick={() => logoInputRef.current?.click()}
                        title={t("account_avatar.change")}
                    >
                        {logoPreview ? (
                            <img
                                src={logoPreview}
                                alt=""
                                className="h-full w-full rounded-full object-cover"
                            />
                        ) : (
                            (name || id || "E").trim().charAt(0).toUpperCase()
                        )}
                        <div className="absolute -right-1 -top-1 flex h-6 w-6 items-center justify-center rounded-full bg-white text-primary shadow-sm transition-transform group-hover:scale-110">
                            <Icon value="pencil" size={13} />
                        </div>
                    </button>
                </div>
                <div className="flex items-end gap-3">
                    <Input
                        label={t("companies.brand_color")}
                        type="color"
                        value={brandColor}
                        onChange={(e) => setBrandColor(e.target.value)}
                        className="h-10 w-16 cursor-pointer p-1"
                    />
                    {/*
                    <p className="pb-2 text-xs text-slate-500 dark:text-zinc-400">
                        {t("companies.brand_color_hint")}
                    </p>
                    */}
                </div>
            </div>
            <ImageCropModal
                isOpen={isLogoCropOpen}
                file={logoFile}
                title={t("companies.logo")}
                onClose={() => setIsLogoCropOpen(false)}
                onConfirm={async (file) => {
                    setLogoFile(file);
                    setIsLogoCropOpen(false);
                }}
            />
        </Modal>
    );
};
